import styles from "lib/components/PortalModal.module.scss";
import type { JSX, ReactNode } from "react";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";
import Tooltip from "ui/popovers/Tooltip";
import { Message } from "ui/text/Message";

export interface BuilderModalStep {
  header: string;
  subheader?: ReactNode;
  body: ReactNode | (() => JSX.Element);
  submittable: boolean;
  notSubmittableTooltipText?: string;
  indicateRequiredFields?: boolean;
  progress?: number;
  nextButtonText?: string;
  submitButtonText?: string;
  submittingText?: string;
  modalProps?: Partial<ModalProps>;
}

interface BuilderModalSteppedProps {
  steps: BuilderModalStep[];
  onSubmit: (setIsSaving?: (arg0: boolean) => void) => void;
  modalProps: ModalProps;
  fullHeight?: boolean;
  progress?: number;
  error?: string;
  currentStep?: number;
  setCurrentStep?: (step: number) => void;
}

export default function BuilderModalStepped({
  steps,
  onSubmit,
  modalProps,
  fullHeight,
  progress,
  error,
  currentStep,
  setCurrentStep,
}: BuilderModalSteppedProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [internalStep, setInternalStep] = useState(0);
  const progressPct = typeof progress === "number" ? Math.floor(progress * 100) : 0;

  // Use controlled step if provided, otherwise use internal state
  const step = currentStep !== undefined ? currentStep : internalStep;
  const updateStep = (newStep: number): void => {
    if (setCurrentStep) {
      setCurrentStep(newStep);
    } else {
      setInternalStep(newStep);
    }
  };

  useEffect(() => {
    setIsSaving(false);
    if (currentStep === undefined) {
      setInternalStep(0);
    }
  }, [modalProps.open, currentStep]);

  const {
    header,
    subheader,
    body,
    indicateRequiredFields,
    submittable,
    nextButtonText = "Next",
    submitButtonText = "Submit",
    submittingText = "Submitting",
    notSubmittableTooltipText,
  } = steps[step] ?? {};

  const pastFirstStep = steps.length > 1 && step !== 0;

  return (
    <Modal
      {...modalProps}
      {...steps[step]?.modalProps}
      title={header}
      fullHeight={fullHeight}
      submitting={isSaving}
      variants={["bodyPadding", "footerButtons"]}
      footer={
        <>
          {(progressPct > 0 || error) && (
            <div className="footer-progress">
              <progress className={`${error ? "error" : ""}`} max={100} value={progressPct} />
            </div>
          )}
          <div style={{ marginRight: "auto" }}>
            {progressPct > 0 || error ? (
              <div className={`footer-progress-display ${error ? "error" : ""}`}>
                <span>{error ? error : `${progressPct}%`}</span>
              </div>
            ) : (
              <>
                {steps.length > 1 && (
                  <Button btnSize="large" disabled>
                    <span style={{ color: "#616161" }}>{`Step ${step + 1} of ${steps.length}`}</span>
                  </Button>
                )}
              </>
            )}
          </div>
          <Button
            btnSize="large"
            btnStyle="tertiary"
            disabled={isSaving && !error}
            onClick={() => {
              if (pastFirstStep) {
                updateStep(step - 1);
              } else {
                modalProps.onClose?.();
              }
            }}
          >
            {pastFirstStep ? "Back" : "Cancel"}
          </Button>
          {steps.length === step + 1 ? (
            <Tooltip.Root delayDuration={0}>
              <Tooltip.Trigger>
                <span>
                  <Button
                    btnSize="large"
                    disabled={!submittable || !!error || isSaving}
                    isPending={isSaving && !error}
                    pendingChildren={submittingText}
                    onClick={() => {
                      if (submittable && !isSaving) {
                        setIsSaving(true);
                        onSubmit(setIsSaving);
                      }
                    }}
                  >
                    {submitButtonText}
                  </Button>
                </span>
              </Tooltip.Trigger>
              {!submittable && notSubmittableTooltipText && (
                <Tooltip.Content>{notSubmittableTooltipText}</Tooltip.Content>
              )}
            </Tooltip.Root>
          ) : (
            <Tooltip.Root delayDuration={0}>
              <Tooltip.Trigger>
                <span>
                  <Button
                    btnSize="large"
                    disabled={!submittable || !!error}
                    onClick={() => {
                      updateStep(step + 1);
                    }}
                  >
                    {nextButtonText}
                  </Button>
                </span>
              </Tooltip.Trigger>
              {!submittable && notSubmittableTooltipText && (
                <Tooltip.Content>{notSubmittableTooltipText}</Tooltip.Content>
              )}
            </Tooltip.Root>
          )}
        </>
      }
    >
      <div className={styles.wrapper}>
        {subheader && <Message marginBottom>{subheader}</Message>}
        {indicateRequiredFields && <span className="required-fields-indicator">Required</span>}
        <div className="modal-body">{typeof body === "function" ? <>{body()}</> : body}</div>
      </div>
    </Modal>
  );
}
